import type { CheckInMark } from '../../../hooks/useCheckIns'
import type { CardStatus } from '../../../components/InterestCard'
import { SetDetailDialog, type SetDetailTab } from '../../../components/SetDetailDialog'
import { TotemAction } from '../../../components/TotemAction'
import { toTotemDisplay } from '../../../lib/totemDisplay'
import { setDetailSubtitle } from '../../../lib/setLabel'
import { useTimeFormat } from '../../../hooks/useTimeFormat'
import type { SetFeed } from '../../../hooks/useSetPosts'
import type { PlacedTotem, TotemMarking } from '../../../hooks/useTotemSlots'
import type { SetInterestEntryPoint } from '../../../lib/analytics'
import type { Set as FestivalSet, Artist, PartialRange, SetPost, UserProfile } from '../../../types'

const EMPTY_POSTS: SetPost[] = []

/** The set's detail dialog, opened from a schedule block. */
export function SetBlockDetail({
  set,
  artists,
  artistNames,
  setTitle,
  status,
  detail,
  setDetail,
  feed,
  totem,
  placedTotems,
  onOpenMember,
  interestedMemberIds,
  goingMemberIds,
  ownPartialRange,
  memberRanges,
  memberCheckIns,
  onUpdateOwnRange,
  onCycleSet,
  userProfiles,
  currentUserId,
}: {
  set: FestivalSet
  artists: Record<string, Artist>
  artistNames: string
  setTitle?: string
  status: CardStatus
  detail: SetDetailTab | null
  setDetail: (tab: SetDetailTab | null) => void
  feed: SetFeed
  totem?: TotemMarking
  placedTotems: PlacedTotem[] | undefined
  onOpenMember?: (uid: string) => void
  interestedMemberIds: string[]
  goingMemberIds: string[]
  ownPartialRange?: PartialRange | null
  memberRanges?: Record<string, PartialRange>
  memberCheckIns?: Record<string, CheckInMark>
  onUpdateOwnRange?: (setId: string, partialRange: PartialRange | null) => void
  onCycleSet: (setId: string, entryPoint: SetInterestEntryPoint) => void
  userProfiles: Record<string, UserProfile>
  currentUserId?: string | null
}) {
  const { formatClockRange } = useTimeFormat()
  const posts = feed.postsBySet.get(set.id) ?? EMPTY_POSTS
  const resolvedArtists = set.artistIds
    .map(id => artists[id])
    .filter((a): a is Artist => !!a)
  const totemAction = totem ? (
    <TotemAction
      setId={set.id}
      setName={artistNames}
      totemMarking={totem}
      placedTotems={placedTotems}
    />
  ) : undefined

  return (
    <SetDetailDialog
      open={detail !== null}
      onOpenChange={open => { if (!open) setDetail(null) }}
      tab={detail ?? 'details'}
      onTabChange={setDetail}
      name={artistNames}
      subtitle={setDetailSubtitle(set, setTitle, formatClockRange)}
      artists={resolvedArtists}
      titled={!!set.title && !setTitle}
      artistless={set.artistIds.length === 0}
      description={set.description}
      note={set.note}
      placeholder={set.placeholder}
      setId={set.id}
      reportSet={set}
      totems={placedTotems && placedTotems.length > 0 ? placedTotems.map(p => toTotemDisplay(p, currentUserId)) : undefined}
      totemAction={totemAction}
      setStartTime={set.startTime}
      setEndTime={set.endTime}
      onUpdatePartialRange={totem?.updateSlotPartialRange ? (groupId, totemId, range) => totem.updateSlotPartialRange(groupId, totemId, set.id, range) : undefined}
      onOpenTotem={totem?.onOpenTotem}
      onOpenMember={onOpenMember}
      interestedMemberIds={interestedMemberIds}
      goingMemberIds={goingMemberIds}
      ownPartialRange={ownPartialRange}
      memberRanges={memberRanges}
      checkedInIds={
        memberCheckIns
          ? new Set(Object.keys(memberCheckIns).filter(uid => memberCheckIns[uid] === 'here'))
          : undefined
      }
      onUpdateOwnRange={
        onUpdateOwnRange ? range => onUpdateOwnRange(set.id, range) : undefined
      }
      onToggle={() => onCycleSet(set.id, 'set_dialog')}
      currentUserStatus={status === 'none' ? null : status}
      posts={posts}
      profiles={userProfiles}
      currentUserId={currentUserId}
      groups={feed.groups}
      isGuest={feed.isGuest}
      onRequireAuth={feed.onRequireAuth}
      onCreatePost={feed.createPost}
      onDeletePost={feed.deletePost}
    />
  )
}
